// client/src/components/teams/BenchmarkEditorPanel.tsx
//
// Panel que permite al coach configurar benchmarks personalizados para su team.
// Los overrides son parciales: dejar un campo vacío = usar el default.
// El tab ALL actúa como fallback global: si no hay override para short/mid/deep
// pero sí hay para all, se aplica el de all.

import React, { useState, useEffect, useCallback } from 'react';
import { useT } from '../../lib/i18n';
import { useTeamBenchmarks } from '../../hooks/useTeamBenchmarks';
import { TeamBenchmarkOverrides, BenchmarkOverride } from '../../contexts/BenchmarkContext';
import { RANGES_BY_STACK, StackKey } from '../stats/leakRanges';
import { RFI_BENCHMARKS } from '../stats/rfiBenchmarks';
import { VPIP_BENCHMARKS } from '../stats/vpipBenchmarks';
import { CBET_TEXTURE_BENCHMARKS } from '../stats/cbetTextureBenchmarks';
import { CBET_TEXTURE_MULTI_BENCHMARKS } from '../stats/cbetTextureMultiBenchmarks';
import { NASH_PUSH_BENCHMARKS } from '../stats/nashPushFoldBenchmarks';
import { FOLD_TO_PUSH_BENCHMARKS } from '../stats/foldToPushBenchmarks';

type Category = 'leakRanges' | 'rfi' | 'vpip' | 'cbetTexture' | 'cbetTextureMulti' | 'pushFold' | 'foldToPush';

const CATEGORIES: { key: Category; label: string }[] = [
  { key: 'leakRanges',       label: 'Rangos Globales'    },
  { key: 'rfi',              label: 'RFI'                },
  { key: 'vpip',             label: 'VPIP pos.'          },
  { key: 'cbetTexture',      label: 'CBet Textura HU'    },
  { key: 'cbetTextureMulti', label: 'CBet Textura Multi' },
  { key: 'pushFold',         label: 'Push/Fold'          },
  { key: 'foldToPush',       label: 'Fold vs Push'       },
];

const STACKS: StackKey[] = ['all', 'short', 'mid', 'deep'];
const STACK_LABELS: Record<StackKey, string> = { all: 'ALL', short: 'SHORT', mid: 'MID', deep: 'DEEP' };

type InputMap = Record<string, { min: string; max: string }>;

function inputFromOverride(ov: BenchmarkOverride | undefined): { min: string; max: string } {
  if (!ov) return { min: '', max: '' };
  return { min: String(ov.min), max: String(ov.max) };
}

function parseOverride(min: string, max: string): BenchmarkOverride | undefined {
  const mn = parseFloat(min);
  const mx = parseFloat(max);
  if (!isNaN(mn) && !isNaN(mx) && mn >= 0 && mx >= mn) return { min: mn, max: mx };
  return undefined;
}

function buildMapFromSavedOverrides(
  saved: Partial<Record<string, BenchmarkOverride>> | undefined,
): InputMap {
  if (!saved) return {};
  return Object.fromEntries(
    Object.entries(saved).map(([k, v]) => [k, inputFromOverride(v)]),
  );
}

function buildStackMapFromSavedOverrides(
  saved: Partial<Record<StackKey, Partial<Record<string, BenchmarkOverride>>>> | undefined,
): Record<StackKey, InputMap> {
  const result = {} as Record<StackKey, InputMap>;
  for (const stack of STACKS) {
    result[stack] = buildMapFromSavedOverrides(saved?.[stack]);
  }
  return result;
}

interface Props {
  teamId: string;
}

export function BenchmarkEditorPanel({ teamId }: Props) {
  const { t } = useT();
  const { overrides: saved, loading, save } = useTeamBenchmarks(teamId);

  const [category,    setCategory]    = useState<Category>('leakRanges');
  const [stackTab,    setStackTab]    = useState<StackKey>('all');
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Estado local por categoría
  const [lrInputs,   setLrInputs]   = useState<Record<StackKey, InputMap>>(() => buildStackMapFromSavedOverrides(undefined));
  const [rfiInputs,  setRfiInputs]  = useState<Record<StackKey, InputMap>>(() => buildStackMapFromSavedOverrides(undefined));
  const [vpipInputs, setVpipInputs] = useState<InputMap>({});
  const [ctInputs,   setCtInputs]   = useState<InputMap>({}); // cbet texture HU
  const [ctmInputs,  setCtmInputs]  = useState<InputMap>({}); // cbet texture multi
  const [pfInputs,   setPfInputs]   = useState<InputMap>({}); // push/fold
  const [ftpInputs,  setFtpInputs]  = useState<InputMap>({}); // fold to push

  // Inicializar desde saved overrides cuando cargan
  useEffect(() => {
    if (!saved) return;
    setLrInputs(buildStackMapFromSavedOverrides(saved.leakRanges));
    setRfiInputs(buildStackMapFromSavedOverrides(saved.rfi));
    setVpipInputs(buildMapFromSavedOverrides(saved.vpip));
    setCtInputs(buildMapFromSavedOverrides(saved.cbetTexture));
    setCtmInputs(buildMapFromSavedOverrides(saved.cbetTextureMulti));
    setPfInputs(buildMapFromSavedOverrides(saved.pushFold));
    setFtpInputs(buildMapFromSavedOverrides(saved.foldToPush));
  }, [saved]);

  // ── Helpers de input ───────────────────────────────────────────────────────

  function setStackInput(
    setter: React.Dispatch<React.SetStateAction<Record<StackKey, InputMap>>>,
    stack: StackKey, key: string, field: 'min' | 'max', val: string,
  ) {
    setter(prev => ({
      ...prev,
      [stack]: { ...prev[stack], [key]: { ...(prev[stack]?.[key] || { min: '', max: '' }), [field]: val } },
    }));
  }

  function setFlatInput(
    setter: React.Dispatch<React.SetStateAction<InputMap>>,
    key: string, field: 'min' | 'max', val: string,
  ) {
    setter(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { min: '', max: '' }), [field]: val },
    }));
  }

  // ── Construir objeto de overrides para guardar ─────────────────────────────

  function buildStackOverrides(
    inputs: Record<StackKey, InputMap>,
  ): Partial<Record<StackKey, Partial<Record<string, BenchmarkOverride>>>> | undefined {
    const result: Partial<Record<StackKey, Partial<Record<string, BenchmarkOverride>>>> = {};
    let hasAny = false;
    for (const stack of STACKS) {
      const stackResult: Partial<Record<string, BenchmarkOverride>> = {};
      for (const [key, { min, max }] of Object.entries(inputs[stack] || {})) {
        const ov = parseOverride(min, max);
        if (ov) { stackResult[key] = ov; hasAny = true; }
      }
      if (Object.keys(stackResult).length > 0) result[stack] = stackResult;
    }
    return hasAny ? result : undefined;
  }

  function buildFlatOverrides(
    inputs: InputMap,
  ): Partial<Record<string, BenchmarkOverride>> | undefined {
    const result: Partial<Record<string, BenchmarkOverride>> = {};
    for (const [key, { min, max }] of Object.entries(inputs)) {
      const ov = parseOverride(min, max);
      if (ov) result[key] = ov;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }

  // ── Guardar ────────────────────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveMsg(null);
    const newOverrides: TeamBenchmarkOverrides = {};
    const lr  = buildStackOverrides(lrInputs);
    const rfi = buildStackOverrides(rfiInputs);
    const vp  = buildFlatOverrides(vpipInputs);
    const ct  = buildFlatOverrides(ctInputs);
    const ctm = buildFlatOverrides(ctmInputs);
    const pf  = buildFlatOverrides(pfInputs);
    const ftp = buildFlatOverrides(ftpInputs);
    if (lr)  newOverrides.leakRanges       = lr;
    if (rfi) newOverrides.rfi              = rfi;
    if (vp)  newOverrides.vpip             = vp;
    if (ct)  newOverrides.cbetTexture      = ct;
    if (ctm) newOverrides.cbetTextureMulti = ctm;
    if (pf)  newOverrides.pushFold         = pf;
    if (ftp) newOverrides.foldToPush       = ftp;
    const ok = await save(newOverrides);
    setSaving(false);
    setSaveMsg(ok
      ? { type: 'ok',  text: t('teams.benchmarks.savedOk') }
      : { type: 'err', text: t('teams.benchmarks.savedErr') },
    );
    setTimeout(() => setSaveMsg(null), 3000);
  }, [save, lrInputs, rfiInputs, vpipInputs, ctInputs, ctmInputs, pfInputs, ftpInputs, t]);

  // ── Resetear categoría activa ─────────────────────────────────────────────

  const handleResetCategory = useCallback(() => {
    if (category === 'leakRanges')       setLrInputs(buildStackMapFromSavedOverrides(undefined));
    else if (category === 'rfi')         setRfiInputs(buildStackMapFromSavedOverrides(undefined));
    else if (category === 'vpip')        setVpipInputs({});
    else if (category === 'cbetTexture') setCtInputs({});
    else if (category === 'cbetTextureMulti') setCtmInputs({});
    else if (category === 'pushFold')    setPfInputs({});
    else if (category === 'foldToPush')  setFtpInputs({});
  }, [category]);

  // ── Estilos ────────────────────────────────────────────────────────────────

  const panelStyle: React.CSSProperties = {
    background: '#111520', border: '1px solid #1e2636', borderRadius: '8px',
    padding: '1.25rem', marginTop: '1rem', fontFamily: 'var(--mono)',
  };
  const tableStyle: React.CSSProperties = {
    width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem',
  };
  const thStyle: React.CSSProperties = {
    fontSize: '0.62rem', letterSpacing: '1.5px', textTransform: 'uppercase',
    color: 'var(--muted)', padding: '0.4rem 0.5rem', textAlign: 'left',
    borderBottom: '1px solid #1e2636',
  };
  const tdStyle: React.CSSProperties = {
    padding: '0.35rem 0.5rem', borderBottom: '1px solid #0d1117', color: 'var(--text)',
  };
  const tdMuted: React.CSSProperties = { ...tdStyle, color: 'var(--muted)' };
  const inputStyle: React.CSSProperties = {
    width: '52px', background: '#0d1117', border: '1px solid #1e2636',
    borderRadius: '3px', padding: '0.25rem 0.4rem', color: 'var(--text)',
    fontFamily: 'var(--mono)', fontSize: '0.78rem', textAlign: 'right' as const,
  };
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.3rem 0.7rem', borderRadius: '4px', cursor: 'pointer',
    fontSize: '0.72rem', fontFamily: 'var(--display)', fontWeight: 700,
    letterSpacing: '0.5px', border: 'none',
    background: active ? '#4a9ae0' : 'transparent',
    color: active ? '#fff' : 'var(--muted)',
  });

  // ── Render de tablas por categoría ────────────────────────────────────────

  function renderStackTabs() {
    return (
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem' }}>
        {STACKS.map(s => (
          <button key={s} style={tabStyle(stackTab === s)} onClick={() => setStackTab(s)}>
            {STACK_LABELS[s]}
          </button>
        ))}
        <span style={{ fontSize: '0.65rem', color: 'var(--muted)', marginLeft: '0.5rem', alignSelf: 'center' }}>
          {t('teams.benchmarks.allFallbackHint')}
        </span>
      </div>
    );
  }

  function renderInputPair(
    key: string, field: 'min' | 'max',
    value: string,
    onChange: (val: string) => void,
  ) {
    return (
      <input
        type="number" min={0} max={200} step={0.5}
        style={inputStyle}
        value={value}
        placeholder="—"
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  function renderLeakRangesTable() {
    const rows = Object.entries(RANGES_BY_STACK[stackTab] || RANGES_BY_STACK.all);
    const stackMap = lrInputs[stackTab] || {};
    return (
      <>
        {renderStackTabs()}
        <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t('teams.benchmarks.colStat')}</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>{t('teams.benchmarks.colDefault')}</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>{t('teams.benchmarks.colOverrideMin')}</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>{t('teams.benchmarks.colOverrideMax')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, r]) => {
                const v = stackMap[key] || { min: '', max: '' };
                return (
                  <tr key={key}>
                    <td style={tdStyle}>{r.label}</td>
                    <td style={{ ...tdMuted, textAlign: 'right' }}>{r.min}–{r.max}%</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {renderInputPair(key, 'min', v.min, val => setStackInput(setLrInputs, stackTab, key, 'min', val))}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      {renderInputPair(key, 'max', v.max, val => setStackInput(setLrInputs, stackTab, key, 'max', val))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  function renderRfiTable() {
    const rows = Object.entries(RFI_BENCHMARKS[stackTab] || RFI_BENCHMARKS.all);
    const stackMap = rfiInputs[stackTab] || {};
    return (
      <>
        {renderStackTabs()}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{t('teams.benchmarks.colPosition')}</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>{t('teams.benchmarks.colDefault')}</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>{t('teams.benchmarks.colOverrideMin')}</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>{t('teams.benchmarks.colOverrideMax')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([pos, r]) => {
              const v = stackMap[pos] || { min: '', max: '' };
              return (
                <tr key={pos}>
                  <td style={tdStyle}>{pos}</td>
                  <td style={{ ...tdMuted, textAlign: 'right' }}>{r.min}–{r.max}%</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {renderInputPair(pos, 'min', v.min, val => setStackInput(setRfiInputs, stackTab, pos, 'min', val))}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {renderInputPair(pos, 'max', v.max, val => setStackInput(setRfiInputs, stackTab, pos, 'max', val))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }

  function renderSimpleTable(
    rows: Array<{ key: string; label: string; default: { min: number; max: number } }>,
    inputs: InputMap,
    setter: React.Dispatch<React.SetStateAction<InputMap>>,
  ) {
    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>{t('teams.benchmarks.colStat')}</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>{t('teams.benchmarks.colDefault')}</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>{t('teams.benchmarks.colOverrideMin')}</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>{t('teams.benchmarks.colOverrideMax')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label, default: def }) => {
            const v = inputs[key] || { min: '', max: '' };
            return (
              <tr key={key}>
                <td style={tdStyle}>{label}</td>
                <td style={{ ...tdMuted, textAlign: 'right' }}>{def.min}–{def.max}%</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {renderInputPair(key, 'min', v.min, val => setFlatInput(setter, key, 'min', val))}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {renderInputPair(key, 'max', v.max, val => setFlatInput(setter, key, 'max', val))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderCategoryContent() {
    switch (category) {
      case 'leakRanges': return renderLeakRangesTable();
      case 'rfi':        return renderRfiTable();

      case 'vpip': return renderSimpleTable(
        Object.entries(VPIP_BENCHMARKS).map(([k, v]) => ({ key: k, label: k, default: v })),
        vpipInputs, setVpipInputs,
      );

      case 'cbetTexture': return renderSimpleTable(
        Object.entries(CBET_TEXTURE_BENCHMARKS).map(([k, v]) => ({
          key: k,
          label: k.replace('-', ' ').replace('-', ' '),
          default: v,
        })),
        ctInputs, setCtInputs,
      );

      case 'cbetTextureMulti': return renderSimpleTable(
        Object.entries(CBET_TEXTURE_MULTI_BENCHMARKS).map(([k, v]) => ({
          key: k, label: k, default: v,
        })),
        ctmInputs, setCtmInputs,
      );

      case 'pushFold': return renderSimpleTable(
        Object.entries(NASH_PUSH_BENCHMARKS).map(([k, v]) => ({
          key: k, label: k, default: v,
        })),
        pfInputs, setPfInputs,
      );

      case 'foldToPush': {
        const labels: Record<string, string> = { micro: '≤10bb', short: '10–20bb', mid: '20–30bb' };
        return renderSimpleTable(
          Object.entries(FOLD_TO_PUSH_BENCHMARKS).map(([k, v]) => ({
            key: k, label: labels[k] || k, default: v!,
          })),
          ftpInputs, setFtpInputs,
        );
      }

      default: return null;
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '1.5rem', color: 'var(--muted)', fontSize: '0.82rem', fontFamily: 'var(--mono)' }}>
        {t('teams.benchmarks.loading')}
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
          {t('teams.benchmarks.title')}
        </div>
        <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--muted)' }}>
          {t('teams.benchmarks.subtitle')}
        </p>
      </div>

      {/* Tabs de categoría */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
        {CATEGORIES.map(c => (
          <button key={c.key} style={tabStyle(category === c.key)} onClick={() => setCategory(c.key)}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Contenido de la categoría activa */}
      {renderCategoryContent()}

      {/* Acciones */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('teams.benchmarks.saving') : t('teams.benchmarks.saveBtn')}
        </button>
        <button
          className="btn btn-secondary"
          onClick={handleResetCategory}
          disabled={saving}
        >
          {t('teams.benchmarks.resetCategory')}
        </button>
        {saveMsg && (
          <span style={{ fontSize: '0.78rem', color: saveMsg.type === 'ok' ? '#52c2a0' : '#e05252' }}>
            {saveMsg.text}
          </span>
        )}
      </div>

      <p style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: 'var(--muted)' }}>
        {t('teams.benchmarks.footerHint')}
      </p>
    </div>
  );
}
